import { useCallback, useEffect, useState } from "react";
import { fetchChatAccess } from "../services/teamChatService";
import {
    readCachedTeamChatAccess,
    writeCachedTeamChatAccess,
} from "./teamChatAccessCache";

/**
 * Hide Instant Chat until the access check finishes, unless a previous
 * successful check for this user already said it is allowed.
 * After a failed load, show the overlay (the API still blocks use).
 */
export function isTeamChatVisible(loading: boolean, available: boolean | null): boolean {
    if (available === true) {
        return true;
    }
    if (available === false) {
        return false;
    }
    return !loading;
}

/**
 * Loads whether the signed-in user may see Instant Chat.
 */
export function useTeamChatAccess(): {
    available: boolean | null;
    visible: boolean;
    loading: boolean;
    error: Error | null;
} {
    const [available, setAvailable] = useState<boolean | null>(() => readCachedTeamChatAccess());
    const [loading, setLoading] = useState(() => readCachedTeamChatAccess() === null);
    const [error, setError] = useState<Error | null>(null);

    const load = useCallback(async () => {
        const cached = readCachedTeamChatAccess();
        if (cached === null) {
            setLoading(true);
        }
        try {
            const result = await fetchChatAccess();
            setAvailable(result.available);
            writeCachedTeamChatAccess(result.available);
            setError(null);
        } catch (err) {
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    return {
        available,
        visible: isTeamChatVisible(loading, available),
        loading,
        error,
    };
}
